import React, { useState } from 'react';

interface UrlInputStepProps {
  onNext: (url: string) => void;
  initialUrl?: string;
}

// --- Tailwind class constants (旧 styled-components) ---

const CONTAINER = 'w-full';

const TITLE =
  'text-[1.625rem] font-bold text-gray-800 mb-3 text-center leading-[1.4] max-[480px]:text-[1.375rem] max-[480px]:[&_br]:hidden';

const SUBTITLE = 'text-[0.9375rem] text-gray-500 mb-8 text-center leading-[1.6]';

const FORM = 'w-full';

const URL_INPUT_CONTAINER = 'relative mb-2';

const URL_INPUT_BASE =
  'w-full py-[14px] px-4 text-[1rem] rounded-lg border-2 bg-white text-gray-800 transition-[border-color,box-shadow] duration-200 ease-[ease] outline-none placeholder:text-gray-400';
const URL_INPUT_ERROR = 'border-red-600 focus:border-red-600';
const URL_INPUT_FOCUSED =
  'border-violet-600 focus:border-violet-600 animate-[obc-glow-pulse_2s_ease-in-out_infinite]';
const URL_INPUT_IDLE = 'border-gray-300 focus:border-violet-600';

const ERROR_TEXT = 'mt-1.5 text-[0.8125rem] text-red-600';

const SUBMIT_BUTTON_BASE =
  'w-full py-[14px] px-6 text-[1.0625rem] font-semibold text-white border-0 rounded-lg transition-all duration-200 ease-[ease] mt-5';
const SUBMIT_BUTTON_DISABLED = 'bg-gray-300 cursor-not-allowed';
const SUBMIT_BUTTON_ENABLED =
  'bg-violet-600 cursor-pointer hover:bg-violet-700 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)] active:translate-y-0 active:shadow-none';

const SOCIAL_PROOF_SECTION = 'mt-8 text-center';

const SOCIAL_PROOF_TEXT = 'text-[0.8125rem] text-gray-400 mb-3';

const COMPANY_LOGOS = 'flex justify-center items-center gap-5 flex-wrap';

const COMPANY_LOGO_TEXT =
  'text-gray-400 tracking-[0.05em] whitespace-nowrap select-none';

export const UrlInputStep: React.FC<UrlInputStepProps> = ({ onNext, initialUrl = '' }) => {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState<string | undefined>();
  const [isFocused, setIsFocused] = useState(false);

  const validateUrl = (value: string): boolean => {
    if (!value.trim()) {
      setError('URLを入力してください');
      return false;
    }

    // Must start with http:// or https://
    if (!/^https?:\/\//i.test(value)) {
      setError('URLはhttp://またはhttps://で始めてください');
      return false;
    }

    try {
      const urlObj = new URL(value);
      if (!urlObj.hostname.includes('.')) {
        setError('有効なURLを入力してください');
        return false;
      }
    } catch {
      setError('有効なURLを入力してください');
      return false;
    }

    setError(undefined);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateUrl(url)) {
      onNext(url);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrl(value);
    if (error) {
      setError(undefined);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (url.trim()) {
      validateUrl(url);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const hasError = !!error;
  const isDisabled = !url.trim();

  const urlInputStateClass = hasError
    ? URL_INPUT_ERROR
    : isFocused
      ? URL_INPUT_FOCUSED
      : URL_INPUT_IDLE;

  return (
    <div className={CONTAINER}>
      <h2 className={TITLE}>あなたのサービスのバナーを<br />AIが30秒で作ります</h2>
      <p className={SUBTITLE}>
        URLを入力するだけで、AIがあなた専用のバナーを自動生成します
      </p>

      <form className={FORM} onSubmit={handleSubmit}>
        <div className={URL_INPUT_CONTAINER}>
          <input
            className={`${URL_INPUT_BASE} ${urlInputStateClass}`}
            type="url"
            value={url}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder="https://your-service.com"
            autoComplete="url"
          />
          {error && <div className={ERROR_TEXT}>{error}</div>}
        </div>

        <button
          className={`${SUBMIT_BUTTON_BASE} ${isDisabled ? SUBMIT_BUTTON_DISABLED : SUBMIT_BUTTON_ENABLED}`}
          type="submit"
          disabled={isDisabled}
        >
          無料でバナーを作る
        </button>
      </form>

      <div className={SOCIAL_PROOF_SECTION}>
        <p className={SOCIAL_PROOF_TEXT}>1,200社以上が利用中</p>
        <div className={COMPANY_LOGOS}>
          <span className={`${COMPANY_LOGO_TEXT} text-[0.9375rem] font-bold`}>BECAUSE</span>
          <span className={`${COMPANY_LOGO_TEXT} text-[0.75rem] font-medium`}>Orvion Co.</span>
          <span className={`${COMPANY_LOGO_TEXT} text-[0.875rem] font-extrabold`}>NEXTERA</span>
          <span className={`${COMPANY_LOGO_TEXT} text-[0.8125rem] font-semibold`}>Platto Inc.</span>
          <span className={`${COMPANY_LOGO_TEXT} text-[0.6875rem] font-medium`}>VELTRA MEDIA</span>
        </div>
      </div>
    </div>
  );
};

export default UrlInputStep;
